import {chains} from "../configs";
import { formatUnits, type BigNumberish } from 'ethers';

export function getDisplayAmount(inputAmount: BigNumberish, decimals: string | BigNumberish) : number {
	return Number(formatUnits(inputAmount, decimals));
}

export function denyExtraFraction(value?: number, fraction = 6, toLocalString = false) : string{
	if(!Number(value) && Number(value) !== 0) {
		return ''
	}
	const result = Math.floor(Number(value) * (10 ** fraction)) / (10 ** fraction)
	if (toLocalString) {
		return result.toLocaleString(undefined, { maximumFractionDigits : fraction })
	}
	return String(result);
}

export function denySmartExtraFraction(input?: number | string | null, toLocalString = false) : string {
	if(!Number(input) && Number(input) !== 0) {
		return ''
	}
	const value = Number(input);
	let precision =  5
	if(value < 1) {
		const str = String(value);
		const x = str.split('.');
		precision = x[1] ? Math.max(x[1].search(/[1-9]/) + 3, 5) : 0
	}
	const fraction = Math.max(2, precision - value.toFixed().length)
	const result = Math.floor(Number(value) * (10 ** fraction)) / (10 ** fraction)
	if (toLocalString) {
		return result.toLocaleString(undefined, { maximumFractionDigits : fraction })
	}
	return String(result);
}

export function getChainByChainName(chainName: ChainName) {
	return chains.find(c => c.nameId === chainName);
}


export function getChainByChainId(chainId: number): Chain | undefined {
	return  chains.find(c => c.chainId === chainId);
}

export function getChainBywChainId(wChainId: number) : Chain {
	const result = chains.find(c => c.wChainId === wChainId);
	if (!result) {
		console.error(`Chain with wChainId ${wChainId} not found, return default chain ${chains[0].chainName}`);
		return chains[0];
	}
	return result;
}

export function parseTradeDetailsResponse(input: any) : TradeDetailsResponse {
	const sourceChain = getChainBywChainId(Number(input.sourceChain) as number);
	const destChain = getChainBywChainId(Number(input.destChain) as number);
	const refundChain = getChainBywChainId(Number(input.refundChain) as number);
	const swapChain = getChainBywChainId(Number(input.swapChain) as number);
	const fromTokenChain = getChainBywChainId(Number(input.fromTokenChain) as number);
	const toTokenChain = getChainBywChainId(Number(input.toTokenChain) as number);
	return {
		bridgeFee: Number(input.bridgeFee),
		completedAt: input.completedAt,
		redeemRelayerFee: Number(input.redeemRelayerFee),
		refundRelayerFee: Number(input.refundRelayerFee),
		fromAmount: Number(input.fromAmount),
		refundAmount: Number(input.refundAmount),
		refundTokenLogoUri: input.refundTokenLogoUri,
		refundTokenScannerUrl: input.refundTokenScannerUrl,
		refundTokenSymbol: input.refundTokenSymbol,
		fromTokenAddress: input.fromTokenAddress,
		fromTokenChain,
		fromTokenLogoUri: input.fromTokenLogoUri,
		fromTokenScannerUrl: input.fromTokenScannerUrl,
		fromTokenSymbol: input.fromTokenSymbol,
		id: input.orderId,
		initiatedAt: input.initiatedAt,
		sourceChain,
		swapSequence: input.swapSequence,
		redeemResult: input.redeemResult,
		redeemSequence: input.redeemSequence,
		redeemSignedVaa: input.redeemSignedVaa,
		refundSignedVaa: input.refundSignedVaa,
		redeemTxHash: input.redeemTxHash,
		redeemTxFee: input.redeemTxFee ? getDisplayAmount(input.redeemTxFee, sourceChain.baseToken.decimals) : undefined,
		savedAt: input.savedAt,
		statusUpdatedAt: input.statusUpdatedAt,
		syncRequestedAt: input.syncRequestedAt,
		swapRelayerFee: Number(input.swapRelayerFee),
		status: input.status,
		swapChain,
		toAmount: Number(input.toAmount),
		toTokenAddress: input.toTokenAddress,
		toTokenChain,
		toTokenLogoUri: input.toTokenLogoUri,
		toTokenScannerUrl: input.toTokenScannerUrl,
		toTokenSymbol: input.toTokenSymbol,
		swapSignedVaa: input.swapSignedVaa,
		trader: input.trader,
		traderScannerUrl: `${sourceChain.blockExplorer}/${sourceChain.wChainId === 1 || sourceChain.wChainId === 21 ? 'account': 'address'}/${input.trader}`,
		destinationScannerUrl: `${destChain.blockExplorer}/${destChain.wChainId === 1 || destChain.wChainId === 21 ? 'account' : 'address'}/${input.destAddress}`,
		transferSignedVaa: input.transferSignedVaa,
		fulfillSignedVaa: input.fulfillSignedVaa,
		sourceTxHash: input.sourceTxHash,
		fulfillTxHash: input.fulfillTxHash,
		sourceTxFee: input.sourceTxFee ? getDisplayAmount(input.sourceTxFee, sourceChain.baseToken.decimals) : 0,
		transferSequence: input.transferSequence,
		sourceTxBlockNo: input.sourceTxBlockNo,
		driverAddress: input.driverAddress ? input.driverAddress : null,
		destChain,
		refundChain,
		destAddress: input.destAddress ? input.destAddress : null,
		txs: input.txs || [],
		isCompleted: input?.clientStatus === 'COMPLETED',
		isInProgress: input?.clientStatus === 'INPROGRESS',
		isRefunded: input?.clientStatus === 'REFUNDED',
		isCanceled: input?.clientStatus === 'CANCELED',
		service: input.service,
		referrerAddress: input.referrerAddress,
		gasDrop: input.gasDrop,
		steps: input.steps,
		clientStatus: input.clientStatus,
		orderId: input.orderId,
		auctionAddress: input.auctionAddress || '',
		auctionStateAddr: input.auctionStateAddr || '',
		security: input.security || null,
	}
}

export function parseTradeListItemResponse(input: any) : TradeListItemResponse {
	const sourceChain = getChainBywChainId(Number(input.sourceChain) as number);
	const destChain = getChainBywChainId(Number(input.destChain) as number);
	const swapChain = getChainBywChainId(Number(input.swapChain) as number);
	return {
		fromAmount: Number(input.fromAmount),
		destChain,
		fromTokenSymbol: input.fromTokenSymbol,
		id: input.orderId,
		initiatedAt: input.initiatedAt,
		toTokenAddress: input.toTokenAddress,
		sourceChain,
		status: input.status,
		swapChain,
		toAmount: Number(input.toAmount),
		toTokenSymbol: input.toTokenSymbol,
		trader: input.trader,
		sourceTxHash: input.sourceTxHash,
		orderId: input.orderId,
		isCompleted: input?.clientStatus === 'COMPLETED',
		isRefunded: input?.clientStatus === 'REFUNDED',
		isCanceled: input?.clientStatus === 'CANCELED',
		service: input.service,
		clientStatus: input.clientStatus,
	}
}

export async function getCurrentSolanaTime() : Promise<number> {
	const res = await fetch(`${process.env.NEXT_PUBLIC_MAYAN_PRICE_URL}/clock/solana`);
	const result = await res.json();
	if (res.status !== 200) {
		throw result;
	}
	return result.clock;
}

export function getShortAddress(address?: string) :string {
	if (!address || address?.length < 12) {
		return address || '';
	}
	return address.slice(0, 6) + '...' + address.slice(address.length - 4)
}
